
export interface NodeTypeConfig {
  label: string
  shape: string
  color: string
}

// #4612/#4646: NODE_TYPES/EDGE_TYPES are split into INVENTION_*/PATENT_* so
// ConnectionsLegend can default to "today's invention-graph keys only" via
// Object.keys(INVENTION_NODE_TYPES) — a single source of truth, no manual
// key-list to keep in sync — while still exporting one flat NODE_TYPES for
// every existing importer (ConnectionsGraph's cytoscape style generation,
// NodeDetailPanel's lookup) exactly as before (spread preserves key order and
// values; Object.entries/property lookup behave identically on the result).
export const INVENTION_NODE_TYPES: Record<string, NodeTypeConfig> = {
  invention: { label: 'Invention', shape: 'star', color: '#0EA5E9' },
  journal: { label: 'Journal', shape: 'hexagon', color: '#8B5CF6' },
  project: { label: 'Project', shape: 'roundrectangle', color: '#10B981' },
  collaborator: { label: 'Collaborator', shape: 'ellipse', color: '#F59E0B' },
  topic: { label: 'Topic', shape: 'diamond', color: '#6B7280' },
  discovery: { label: 'Discovery', shape: 'pentagon', color: '#EC4899' },
  entity: { label: 'Entity', shape: 'diamond', color: '#14B8A6' },
  document: { label: 'Document', shape: 'rectangle', color: '#0EA5E9' },
}

// #4612/#4646: Patent Connections graph node types (patent record detail's
// Connections tab). Zero key collision with INVENTION_NODE_TYPES (verified).
export const PATENT_NODE_TYPES: Record<string, NodeTypeConfig> = {
  patent: { label: 'This patent', shape: 'star', color: '#0EA5E9' },
  similar_patent: { label: 'Similar patent', shape: 'diamond', color: '#38BDF8' },
  inventor: { label: 'Inventor', shape: 'ellipse', color: '#F59E0B' },
  assignee: { label: 'Assignee', shape: 'hexagon', color: '#8B5CF6' },
  cpc: { label: 'CPC category', shape: 'rectangle', color: '#64748B' },
  family: { label: 'Family member', shape: 'round-triangle', color: '#10B981' },
}

export const NODE_TYPES: Record<string, NodeTypeConfig> = {
  ...INVENTION_NODE_TYPES,
  ...PATENT_NODE_TYPES,
}

// Subtype-specific colours for entity nodes (used by ConnectionsGraph) —
// invention-graph only; the patent graph has no 'entity' node type, so this
// stays a single (unsplit) map, gated in ConnectionsLegend by whether
// 'entity' itself is in the active nodeTypeKeys scope.
export const ENTITY_SUBTYPE_STYLES: Record<string, { color: string; label: string }> = {
  material: { color: '#14B8A6', label: 'Material' },
  method: { color: '#F59E0B', label: 'Method' },
  application: { color: '#F43F5E', label: 'Application' },
}

export const INVENTION_EDGE_TYPES: Record<string, { label: string; color: string; dashed?: boolean }> = {
  cites: { label: 'Cites', color: '#94A3B8' },
  related: { label: 'Related (general)', color: '#6366F1' },
  similar_invention: { label: 'Similar invention', color: '#0EA5E9' },
  related_research: { label: 'Related research', color: '#8B5CF6' },
  related_invention: { label: 'Related invention', color: '#0284C7' },
  related_journal: { label: 'Related journal', color: '#A855F7' },
  contains: { label: 'Contains', color: '#10B981' },
  collaborates: { label: 'Collaborates', color: '#F59E0B' },
  tagged: { label: 'Tagged', color: '#6B7280' },
  discovered: { label: 'Discovered', color: '#EC4899' },
  uses_material: { label: 'Uses Material', color: '#14B8A6' },
  uses_method: { label: 'Uses Method', color: '#F59E0B' },
  applies_to: { label: 'Applies To', color: '#F43F5E' },
  shared_entity: { label: 'Shared Entity', color: '#8B5CF6', dashed: true },
  same_project: { label: 'Same Project', color: '#D1D5DB', dashed: true },
}

// #4612/#4646: Patent Connections graph edge types. Zero key collision with
// INVENTION_EDGE_TYPES (verified).
export const PATENT_EDGE_TYPES: Record<string, { label: string; color: string; dashed?: boolean }> = {
  similar_patent: { label: 'Similar patent', color: '#38BDF8' },
  invented_by: { label: 'Invented by', color: '#F59E0B' },
  assigned_to: { label: 'Assigned to', color: '#8B5CF6', dashed: true },
  classified_as: { label: 'Classified as', color: '#64748B', dashed: true },
  same_family: { label: 'Same family', color: '#10B981' },
}

export const EDGE_TYPES: Record<string, { label: string; color: string; dashed?: boolean }> = {
  ...INVENTION_EDGE_TYPES,
  ...PATENT_EDGE_TYPES,
}
